import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  TableHead,
  IconButton,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { getBackend, putBackend, deleteBackend } from "../../api/backend";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { useTheme } from "@mui/material/styles";
import { Link } from 'react-router-dom';
import { fetchWithCache } from "../../utils/cacheUtils";

const MachineDetails = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [machineData, setMachineData] = useState([]);
  const [tempMachineData, setTempMachineData] = useState({});
  const [characteristicsValues, setCharacteristicsValues] = useState([]);
  const [characteristics, setCharacteristics] = useState([]);
  const [machineBrands, setMachineBrands] = useState([]);
  const [machineTypes, setMachineTypes] = useState([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editChars, setEditChars] = useState(false);
  const [editMachine, setEditMachine] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [tempChars, setTempChars] = useState({});
  const theme = useTheme();
  const handleOnCharChange = (uuid, value) => {
    setTempChars((prevState) => ({
      ...prevState,
      [uuid]: value,
    }));
  };

  const hasValidValues = (obj) => {
    const hasValidValues = Object.values(obj).every((value) => {
      return value !== null && value !== undefined && value !== "";
    });

    if (!hasValidValues) {
      alert(
        "No se pueden guardar los cambios sin antes completar todos los campos"
      );
      return false;
    }
    return true;
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const handleOpenConfirm = (items) => {
    setOpenConfirm(true);
  };

  const handleDelete = async () => {
    await deleteBackend("machines", machineData.uuid);
    setOpenConfirm(false);
    navigate(-1);
  };

  const handleOnSaveChars = async () => {
    //if (!hasValidValues(tempChars)) return; // Check values are not empty

    for (const [uuid, value] of Object.entries(tempChars)) {
      let body = characteristicsValues.find((charact) => charact.uuid === uuid);
      if (body) {
        body.value = value;
        await putBackend("machine_characteristic_values", body);
      }
    }
    setEditChars(false);
  };

  const handleOnSaveMachine = async () => {
    if (!hasValidValues(tempMachineData)) return;
    const response = await putBackend("machines", tempMachineData);
    setMachineData(response);
    setEditMachine(false);
  };

  const fetchData = async () => {
    setLoading(true);
    
    try {
      // Fetch machine-specific data (not cached)
      const machinesResponse = await getBackend(`machines/${uuid}`);
      const charsValuesResponse = await getBackend(
        `machine_characteristic_values/?machine_uuid=${uuid}`
      );
      const workOrdersResponse = await getBackend(
        `work_orders/?machine_uuid=${uuid}`
      );
      
      // Fetch and cache reference data
      const machineTypesResponse = await fetchWithCache('machineTypes', 
        () => getBackend("machine_types"));
      
      const machineBrandsResponse = await fetchWithCache('machineBrands', 
        () => getBackend("machine_brands"));
      
      const characteristicsResponse = await fetchWithCache('machineCharacteristics', 
        () => getBackend("machine_characteristics"));
      
      const clientsResponse = await fetchWithCache('clients', 
        () => getBackend("clients"));
      
      const statusesResponse = await fetchWithCache('statuses', 
        () => getBackend("statuses"));
  
      // Set state with fetched data
      setMachineData(machinesResponse);
      setCharacteristics(characteristicsResponse);
      setWorkOrders(workOrdersResponse);
      setClients(
        Object.fromEntries(clientsResponse.map((item) => [item.uuid, item.name]))
      );
      setMachineTypes(
        Object.fromEntries(
          machineTypesResponse.map((item) => [
            item.uuid,
            `${item.type} ${item.subtype} ${item.subsubtype}`,
          ])
        )
      );
      setMachineBrands(
        Object.fromEntries(
          machineBrandsResponse.map((item) => [item.uuid, item.brand])
        )
      );
      setCharacteristicsValues(charsValuesResponse);
      setStatuses(statusesResponse);
    } catch (error) {
      console.error("Error fetching machine data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditMachine = () => {
    setEditMachine(true);
    setTempMachineData(machineData);
    // Initialize filtered options
    setFilteredOptions({
      client_name: Object.values(clients),
      owner_name: Object.values(clients),
      machine_type: Object.values(machineTypes),
      machine_brand: Object.values(machineBrands),
    });
  };

  const handleCancelEditMachine = () => {
    setEditMachine(false);
    setTempMachineData({});
  };

  useEffect(() => {
    fetchData();
  }, [uuid]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const onInputChange = (key, value, options) => {
    let nextValue = [];
    if (options) {
      nextValue = Object.values(options).filter((option) =>
        option.toLowerCase().includes(value.toLowerCase())
      );
    }
    setFilteredOptions((prevState) => {
      if (JSON.stringify(prevState[key]) !== JSON.stringify(nextValue)) {
        return { ...prevState, [key]: nextValue };
      }
      return prevState;
    });
  };

  const handleOnChange = (key, value, options = null) => {
    let nextValue = null;
    if (options) {
      const foundOption = Object.entries(options).find(
        (option) => option[1] === value
      );
      if (foundOption) {
        nextValue = foundOption[0];
      }
    } else nextValue = value;

    setTempMachineData((prevState) => ({
      ...prevState,
      [key]: nextValue,
    }));
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ padding: "16px", marginTop: "3%" }}
    >
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item xs={3} textAlign={"left"}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
        </Grid>
        <Grid item xs={6}>
          <Paper
            elevation={3}
            sx={{ padding: "5px", textAlign: "center", marginBottom: "24px" }}
          >
            <Typography variant="h4" sx={{ marginBottom: "16px" }}>
              Número interno: {machineData.internal_number}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={3} textAlign={"right"}>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleOpenConfirm()}
          >
            {" "}
            <DeleteIcon />
          </Button>
        </Grid>
      </Grid>
      <Card sx={{ boxShadow: 3, marginTop: "8px" }}>
        <CardContent>
          <Grid container justifyContent="space-between">
            <Grid item xs={3}></Grid>
            <Grid item xs={6}>
              <Typography variant="h6" gutterBottom>
                <u>Información</u>
              </Typography>
            </Grid>
            <Grid item xs={3} textAlign={"right"}>
              {!editMachine ? (
                <IconButton onClick={handleEditMachine}>
                  <EditIcon />
                </IconButton>
              ) : (
                <Grid container justifyContent="flex-end">
                  <Grid item xs={2}>
                    <IconButton color="success" onClick={handleOnSaveMachine}>
                      <CheckIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton color="error" onClick={handleCancelEditMachine}>
                      <CloseIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
          <TableContainer
            component={Paper}
            sx={{ boxShadow: "none", borderRadius: "8px" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "light" ? "#f0f0f0" : "#424242",
                      fontWeight: "bold",
                    }}
                  >
                    Tipo
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "light" ? "#f0f0f0" : "#424242",
                      fontWeight: "bold",
                    }}
                  >
                    Código interno
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "light" ? "#f0f0f0" : "#424242",
                      fontWeight: "bold",
                    }}
                  >
                    Código externo
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "light" ? "#f0f0f0" : "#424242",
                      fontWeight: "bold",
                    }}
                  >
                    Nº de placa
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "light" ? "#f0f0f0" : "#424242",
                      fontWeight: "bold",
                    }}
                  >
                    Cliente
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "light" ? "#f0f0f0" : "#424242",
                      fontWeight: "bold",
                    }}
                  >
                    Dueño
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "light" ? "#f0f0f0" : "#424242",
                      fontWeight: "bold",
                    }}
                  >
                    Marca
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {machineTypes[machineData.machine_type] || "N/A"}
                    </Typography>
                  </TableCell>

                  {/* Internal Code */}
                  <TableCell align="center">
                    {editMachine ? (
                      <TextField
                        key={"internal_number"}
                        size="small"
                        sx={{ width: "100px" }}
                        value={tempMachineData.internal_number || ""}
                        onChange={(e) =>
                          handleOnChange("internal_number", e.target.value)
                        }
                      />
                    ) : (
                      machineData.internal_number
                    )}
                  </TableCell>
                  {/* External Code */}
                  <TableCell align="center">
                    {editMachine ? (
                      <TextField
                        key={"external_number"}
                        size="small"
                        sx={{ width: "100px" }}
                        value={tempMachineData.external_number || ""}
                        onChange={(e) =>
                          handleOnChange("external_number", e.target.value)
                        }
                      />
                    ) : (
                      machineData.external_number
                    )}
                  </TableCell>

                  {/* Plate Number */}
                  <TableCell align="center">
                    {editMachine ? (
                      <TextField
                        key={"characteristic_batch_number"}
                        size="small"
                        sx={{ width: "100px" }}
                        value={
                          tempMachineData.characteristic_batch_number || ""
                        }
                        onChange={(e) =>
                          handleOnChange(
                            "characteristic_batch_number",
                            e.target.value
                          )
                        }
                      />
                    ) : (
                      machineData.characteristic_batch_number
                    )}
                  </TableCell>
                  {/* Client Autocomplete */}
                  <TableCell align="center">
                    {editMachine ? (
                      <Autocomplete
                        id="client_name"
                        options={
                          filteredOptions.length > 0
                            ? filteredOptions
                            : Object.values(clients)
                        }
                        getOptionLabel={(option) => option}
                        fullWidth
                        size="small"
                        value={clients[tempMachineData?.client_name] || null}
                        renderInput={(params) => (
                          <TextField {...params} variant="outlined" />
                        )}
                        onChange={(e, value) =>
                          handleOnChange("client_name", value, clients)
                        }
                        onInputChange={(e, value) =>
                          onInputChange("client_name", value, clients)
                        }
                      />
                    ) : (
                      clients[machineData.client_name]
                    )}
                  </TableCell>

                  {/* Owner Autocomplete */}
                  <TableCell align="center">
                    {editMachine ? (
                      <Autocomplete
                        id="owner_name"
                        options={
                          filteredOptions.length > 0
                            ? filteredOptions
                            : Object.values(clients)
                        }
                        getOptionLabel={(option) => option}
                        fullWidth
                        size="small"
                        value={clients[tempMachineData?.owner_name] || null}
                        renderInput={(params) => (
                          <TextField {...params} variant="outlined" />
                        )}
                        onChange={(e, value) =>
                          handleOnChange("owner_name", value, clients)
                        }
                        onInputChange={(e, value) =>
                          onInputChange("owner_name", value, clients)
                        }
                      />
                    ) : (
                      clients[machineData.owner_name]
                    )}
                  </TableCell>

                  {/* Machine Brand Autocomplete */}
                  <TableCell align="center">
                    {editMachine ? (
                      <Autocomplete
                        id="machine_brand"
                        options={filteredOptions.machine_brand}
                        getOptionLabel={(option) => option}
                        fullWidth
                        size="small"
                        value={
                          machineBrands[tempMachineData?.machine_brand] || null
                        }
                        renderInput={(params) => (
                          <TextField {...params} variant="outlined" />
                        )}
                        onChange={(e, value) =>
                          handleOnChange("machine_brand", value, machineBrands)
                        }
                        onInputChange={(e, value) =>
                          onInputChange("machine_brand", value, machineBrands)
                        }
                      />
                    ) : (
                      machineBrands[machineData.machine_brand]
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      <Card sx={{ boxShadow: 3, marginTop: "15px" }}>
        <CardContent>
          <Grid container justifyContent="space-between">
            <Grid item xs={3}></Grid>
            <Grid item xs={6}>
              <Typography variant="h6" gutterBottom>
                <u>Caracteristicas</u>
              </Typography>
            </Grid>
            <Grid item xs={3} textAlign={"right"}>
              {!editChars ? (
                <IconButton onClick={() => setEditChars(true)}>
                  <EditIcon />
                </IconButton>
              ) : (
                <Grid container justifyContent="flex-end">
                  <Grid item xs={2}>
                    {" "}
                    <IconButton
                      color="success"
                      onClick={() => handleOnSaveChars()}
                    >
                      {" "}
                      <CheckIcon />{" "}
                    </IconButton>{" "}
                  </Grid>
                  <Grid item xs={2}>
                    {" "}
                    <IconButton
                      color="error"
                      onClick={() => setEditChars(false)}
                    >
                      {" "}
                      <CloseIcon />{" "}
                    </IconButton>{" "}
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
          <TableContainer
            component={Paper}
            sx={{ boxShadow: "none", borderRadius: "8px" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "light" ? "#f0f0f0" : "#424242",
                      fontWeight: "bold",
                    }}
                  >
                    Caracteristica
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "light" ? "#f0f0f0" : "#424242",
                      fontWeight: "bold",
                    }}
                  >
                    Valor
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "light" ? "#f0f0f0" : "#424242",
                      fontWeight: "bold",
                    }}
                  >
                    Caracteristica
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "light" ? "#f0f0f0" : "#424242",
                      fontWeight: "bold",
                    }}
                  >
                    Valor
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {characteristicsValues.map((characValue, index) => {
                  const characteristic = characteristics.find(
                    (char) => char.uuid === characValue.characteristic
                  );
                  const isEven = index % 2 === 0;

                  return (
                    isEven && (
                      <TableRow key={characValue.uuid}>
                        <TableCell align="center">
                          {characteristic?.characteristic}
                        </TableCell>
                        <TableCell align="center">
                          {editChars ? (
                            <TextField
                              size="small"
                              sx={{ width: "200px" }}
                              defaultValue={characValue.value}
                              onChange={(e) =>
                                handleOnCharChange(
                                  characValue.uuid,
                                  e.target.value
                                )
                              }
                            ></TextField>
                          ) : (
                            characValue.value
                          )}
                        </TableCell>
                        {characteristicsValues[index + 1] && (
                          <>
                            <TableCell align="center">
                              {
                                characteristics.find(
                                  (char) =>
                                    char.uuid ===
                                    characteristicsValues[index + 1]
                                      .characteristic
                                )?.characteristic
                              }
                            </TableCell>
                            <TableCell align="center">
                              {editChars ? (
                                <TextField
                                  size="small"
                                  sx={{ width: "200px" }}
                                  defaultValue={
                                    characteristicsValues[index + 1].value
                                  }
                                  onChange={(e) =>
                                    handleOnCharChange(
                                      characteristicsValues[index + 1].uuid,
                                      e.target.value
                                    )
                                  }
                                ></TextField>
                              ) : (
                                characteristicsValues[index + 1].value
                              )}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    )
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      <Box sx={{ boxShadow: 3, marginTop: "15px" }}>
        <Card>
          <CardContent>
            <Typography variant="h6" align="center" gutterBottom>
              <u>Ordenes de trabajo asociadas</u>
            </Typography>
            <TableContainer
              component={Paper}
              sx={{ boxShadow: "none", borderRadius: "8px" }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                  <TableCell
                      align="center"
                      sx={{
                        backgroundColor:
                          theme.palette.mode === "light"
                            ? "#f0f0f0"
                            : "#424242",
                        fontWeight: "bold",
                      }}
                    >
                      Nº Interno
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor:
                          theme.palette.mode === "light"
                            ? "#f0f0f0"
                            : "#424242",
                        fontWeight: "bold",
                      }}
                    >
                      Descripción
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor:
                          theme.palette.mode === "light"
                            ? "#f0f0f0"
                            : "#424242",
                        fontWeight: "bold",
                      }}
                    >
                      Arribo
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor:
                          theme.palette.mode === "light"
                            ? "#f0f0f0"
                            : "#424242",
                        fontWeight: "bold",
                      }}
                    >
                      Entrega
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        backgroundColor:
                          theme.palette.mode === "light"
                            ? "#f0f0f0"
                            : "#424242",
                        fontWeight: "bold",
                      }}
                    >
                      Estado
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workOrders.map((order, index) => (
                    <TableRow key={index}>
                      <TableCell align="center">

                        <Link to={`/work-orders/${order.uuid}`}>
                          {order.internal_number}
                        </Link>
                      </TableCell>
                      <TableCell align="center">{order.description}</TableCell>
                      <TableCell align="center">{order.arrival_date}</TableCell>
                      <TableCell align="center">{order.pickup_date}</TableCell>
                      <TableCell align="center">
                        {
                          statuses.find(
                            (status) => status.uuid === order.status
                          )?.status
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
      <ConfirmationDialog
        openConfirm={openConfirm}
        handleCloseConfirm={handleCloseConfirm}
        handleDelete={handleDelete}
        itemsToDelete={machineData}
      />
    </Container>
  );
};

export default MachineDetails;
